/* StoryMusicPicker — bottom-sheet picker for royalty-free music tracks.

   IG-style flow:
   1. User taps "Aggiungi musica" in StoryUploader
   2. This sheet slides up from the bottom
   3. Category chips at the top for filtering (Chill / Cheerful / Cinematic /
      Cute / Upbeat / Calm)
   4. Track list with cover thumbnails; tap plays a preview through a shared
      HTMLAudioElement (one at a time)
   5. "Usa questo brano" confirms → parent gets { id, title, artist, url,
      cover_url, duration_s } + optional trim start (defaults to 0)

   The sheet does NOT do server-side muxing. During playback of the final
   Story the caller is expected to layer <audio> synced with the media —
   exactly like Instagram's client-side approach.
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Play, Pause, Music, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Prefix relative asset URLs with the backend origin so <audio> src
// resolves to the same host as our API (matches buildVideoSrc used by
// the Stories player).
function toAbs(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function StoryMusicPicker({ onClose, onSelect, initialTrackId, initialStart = 0 }) {
  const [tracks, setTracks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("Tutte");
  const [selectedId, setSelectedId] = useState(initialTrackId || null);
  const [startSec, setStartSec] = useState(initialStart);
  const [previewId, setPreviewId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stories/music/tracks");
        setTracks(data?.tracks || []);
        setCategories(data?.categories || []);
      } catch {
        setErr("Impossibile caricare la libreria musicale.");
      } finally {
        setLoading(false);
      }
    })();
    return () => { audioRef.current?.pause(); };
  }, []);

  const filtered = useMemo(() => {
    if (category === "Tutte") return tracks;
    return tracks.filter((t) => t.category === category);
  }, [tracks, category]);

  const selected = tracks.find((t) => t.id === selectedId) || null;

  const togglePreview = (t) => {
    const a = audioRef.current;
    if (!a) return;
    if (previewId === t.id) {
      a.pause();
      setPreviewId(null);
      return;
    }
    a.pause();
    a.src = toAbs(t.url);
    a.volume = 0.6;
    a.currentTime = startSec && selectedId === t.id ? startSec : 0;
    a.play().then(() => setPreviewId(t.id)).catch(() => setErr("Riproduzione non riuscita."));
  };

  const confirm = () => {
    if (!selected) return;
    audioRef.current?.pause();
    onSelect({
      id: selected.id,
      title: selected.title,
      artist: selected.artist,
      url: selected.url,
      cover_url: selected.cover_url,
      duration_s: selected.duration_s,
      start_sec: Math.max(0, Math.min(startSec || 0, (selected.duration_s || 30) - 5)),
    });
  };

  const clear = () => {
    audioRef.current?.pause();
    onSelect(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1400] bg-black/70 flex items-end justify-center"
      onClick={onClose}
      data-testid="story-music-picker"
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        className="w-full max-w-lg bg-[#FBF7EF] text-[#1a1a1a] rounded-t-[28px] p-5 pb-8 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <audio ref={audioRef} onEnded={() => setPreviewId(null)} />

        {/* Header */}
        <div className="flex items-center justify-between mb-3 shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#FF7A1A22,#F4B83C22)" }}>
              <Music className="h-5 w-5 text-[#C25612]" />
            </div>
            <div>
              <div className="font-display text-xl">Aggiungi musica</div>
              <div className="text-[12px] text-[#7A7A7A]">Libera da diritti · uso commerciale ok</div>
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center"
            aria-label="Chiudi" data-testid="story-music-picker-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Category chips */}
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2 shrink-0 -mx-5 px-5"
             style={{ WebkitOverflowScrolling: "touch" }}>
          {["Tutte", ...categories].map((c) => {
            const active = c === category;
            return (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`story-music-cat-${c.toLowerCase()}`}
                className="shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border"
                style={{
                  background: active ? "#1A1A1A" : "white",
                  borderColor: active ? "#1A1A1A" : "#F0EBE3",
                  color: active ? "white" : "#1a1a1a",
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {/* Track list */}
        <div className="flex-1 overflow-y-auto -mx-5 px-5 mt-2">
          {loading && (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#FF7A1A]" />
            </div>
          )}
          {err && !loading && (
            <div className="text-[12.5px] text-[#C25612] bg-[#FFEEDD] rounded-xl px-3 py-2 my-2">{err}</div>
          )}
          {!loading && filtered.map((t) => {
            const isSel = t.id === selectedId;
            const isPlay = t.id === previewId;
            return (
              <div key={t.id} data-testid={`story-music-track-${t.id}`}
                   className={`flex items-center gap-3 p-2 rounded-2xl mb-2 border transition-all cursor-pointer ${isSel ? "border-[#FF7A1A]" : "border-transparent"}`}
                   style={{ background: isSel ? "#FFF6E6" : "white" }}
                   onClick={() => setSelectedId(t.id)}>
                {/* Cover */}
                <div className="relative shrink-0">
                  <div className="h-12 w-12 rounded-xl overflow-hidden bg-[#F0EBE3]"
                       style={{ backgroundImage: t.cover_url ? `url(${t.cover_url})` : undefined,
                                backgroundSize: "cover", backgroundPosition: "center" }} />
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePreview(t); }}
                    data-testid={`story-music-preview-${t.id}`}
                    aria-label={isPlay ? "Pausa" : "Ascolta"}
                    className="absolute inset-0 flex items-center justify-center rounded-xl transition-all"
                    style={{ background: "rgba(0,0,0,0.45)" }}
                  >
                    {isPlay ? <Pause className="h-4 w-4 text-white" fill="white" />
                            : <Play className="h-4 w-4 text-white" fill="white" />}
                  </button>
                </div>
                {/* Meta */}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[13.5px] truncate">{t.title}</div>
                  <div className="text-[11px] text-[#7A7A7A] truncate">{t.artist} · {t.category}</div>
                </div>
                {/* Select indicator */}
                <div className="h-6 w-6 rounded-full flex items-center justify-center shrink-0"
                     style={{
                       background: isSel ? "#FF7A1A" : "transparent",
                       border: isSel ? "none" : "1.5px solid #E5DECF",
                     }}>
                  {isSel && <Check className="h-3.5 w-3.5 text-white" />}
                </div>
              </div>
            );
          })}
        </div>

        {/* Trim slider (only when a track is selected) */}
        {selected && (
          <div className="shrink-0 pt-3 border-t border-[#F0EBE3]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] uppercase tracking-widest text-[#9A9A9A]">
                Inizio brano
              </span>
              <span className="text-[11px] font-semibold text-[#1A1A1A] tabular-nums">
                {Math.floor(startSec)}s / {Math.floor(selected.duration_s || 30)}s
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={Math.max(0, (selected.duration_s || 30) - 5)}
              step={0.5}
              value={startSec}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setStartSec(v);
                if (previewId === selected.id && audioRef.current) {
                  audioRef.current.currentTime = v;
                }
              }}
              data-testid="story-music-trim"
              className="w-full accent-[#FF7A1A]"
            />
          </div>
        )}

        {/* Confirm CTA */}
        <div className="flex gap-2 mt-3 shrink-0">
          {selectedId && (
            <button
              onClick={clear}
              data-testid="story-music-clear"
              className="h-11 px-4 rounded-full text-[13px] font-semibold bg-white border border-[#F0EBE3]">
              Rimuovi
            </button>
          )}
          <button
            onClick={confirm}
            disabled={!selectedId}
            data-testid="story-music-confirm"
            className="flex-1 h-11 rounded-full font-bold text-[14px] inline-flex items-center justify-center gap-2"
            style={{
              background: !selectedId ? "#E5DECF"
                : "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
              color: !selectedId ? "#9A9A9A" : "white",
            }}
          >
            <Check className="h-4 w-4" /> Usa questo brano
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
